import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, signal } from '@angular/core';
import { updatedAtSignal } from '../analysis-data';
import { v3ComponentContext } from '../services/v3-component-context';
import { themeAsset } from '../services/theme-asset';
import { toggleSet } from '../services/toggle-set';
import { findV3AnalysisSignal } from '../services/v3-guest-mode';
import { V3_GUEST_MOCK_ANALYSES } from '../services/v3-guest-mode.mock';
import { TimeAgo } from '../time-ago/time-ago';
import { TextCleanPipe } from '../pipes/text-clean-pipe';
import { formatAnalysisText } from '../services/format-analysis-text';
import { V3TabPanel } from '../v3-tab-panel/v3-tab-panel';

interface CatalystCard {
  title: string;
  text: string;
}

interface HeadlineItem {
  title: string;
  date: string | null;
  body: string;
}

// V3's News detail sub-page — V3's counterpart to V2's NewsAnalysisV2, reached from
// V3NewsOverview's "Details"/"Alle News"/"Newsübersicht" (see its CLAUDE.md). Built
// component-by-component; see this component's own CLAUDE.md for what's done where.
@Component({
  selector: 'app-v3-news-analysis',
  imports: [NgTemplateOutlet, TimeAgo, TextCleanPipe, V3TabPanel],
  standalone: true,
  templateUrl: './v3-news-analysis.html',
  styleUrl: './v3-news-analysis.scss',
})
export class V3NewsAnalysis {
  private readonly ctx = v3ComponentContext();

  readonly isZeroDesktop = this.ctx.layout.isZeroDesktop;
  readonly isFinnetDesktop = this.ctx.layout.isFinnetDesktop;
  readonly isFinnet = this.ctx.layout.isFinnet;

  private readonly isGuestMode = this.ctx.isGuestMode;

  private readonly newsAnalysis = findV3AnalysisSignal(
    this.ctx.assetsStore.v2Data,
    'news',
    this.isGuestMode,
  );

  // The page follows the analysis; only the highlight block depends on its title (AI-640).
  readonly visible = computed(() => !!this.newsAnalysis());

  readonly title = computed(() => this.newsAnalysis()?.insights?.['highlight']?.['title'] ?? '');
  readonly description = computed(
    () => this.newsAnalysis()?.insights?.['highlight']?.['body'] ?? '',
  );

  readonly updatedAt = updatedAtSignal(this.newsAnalysis);

  // Katalysatoren — same tabs/filtering as NewsAnalysisV2's catalystTabs/mapCatalysts,
  // kept as its own copy here rather than shared since V2 is expected to go away once V3
  // fully replaces this page (same reasoning as the newsAnalysis lookup above).
  readonly catalystTabs = ['Kurzfristig', 'Langfristig'];
  readonly activeCatalystTab = signal(0);

  private readonly catalystsShortTerm = computed(() => this.mapCatalysts('short_term_catalysts'));
  private readonly catalystsLongTerm = computed(() => this.mapCatalysts('long_term_catalysts'));

  readonly hasCatalysts = computed(
    () => this.catalystsShortTerm().length > 0 || this.catalystsLongTerm().length > 0,
  );

  readonly activeCatalysts = computed(() =>
    this.activeCatalystTab() === 0 ? this.catalystsShortTerm() : this.catalystsLongTerm(),
  );

  selectCatalystTab(index: number) {
    this.activeCatalystTab.set(index);
  }

  // News is public for guests, but the portal restricts both catalyst arrays — so this one
  // needs a field-level mock fallback, unlike the whole-analysis one in findV3AnalysisSignal.
  private mapCatalysts(key: 'short_term_catalysts' | 'long_term_catalysts'): CatalystCard[] {
    const real = this.toCatalystCards(this.newsAnalysis()?.insights?.[key]);
    if (real.length > 0) return real;
    return this.isGuestMode()
      ? this.toCatalystCards(V3_GUEST_MOCK_ANALYSES['news'].insights?.[key])
      : [];
  }

  private toCatalystCards(items: unknown): CatalystCard[] {
    if (!Array.isArray(items)) return [];
    return (items as { restricted?: boolean; title: string; body: string }[])
      .filter((item) => !item.restricted)
      .map((item) => ({ title: item.title, text: item.body }));
  }

  // Top Headlines — same sort/mapping as NewsAnalysisV2's headlines getter (own copy). Title
  // goes through formatAnalysisText (unlike V2's full-title <strong> wrap) since it can
  // contain partial **bold** markup.
  readonly formatText = formatAnalysisText;

  readonly headlines = computed<HeadlineItem[]>(() => {
    const items = this.newsAnalysis()?.insights?.['top_headlines'];
    if (!Array.isArray(items)) return [];
    return (items as { title?: string; date?: string | null; body?: string }[])
      .filter((item) => item.title || item.body)
      .map((item, index) => ({
        headline: { title: item.title ?? '', date: item.date ?? null, body: item.body ?? '' },
        sortTime: this.parseHeadlineDate(item.date),
        index,
      }))
      .sort((left, right) => {
        if (left.sortTime !== right.sortTime) return right.sortTime - left.sortTime;
        return left.index - right.index;
      })
      .map((item) => item.headline);
  });

  readonly hasHeadlines = computed(() => this.headlines().length > 0);

  // Same asset + theme-swap as V3AnalysisHeader's back button, so the dropdown toggle matches
  // it exactly instead of approximating it with a separately-recolored icon.
  readonly backIconSrc = themeAsset(this.ctx.themeService, 'back');

  // Independent per-headline expand/collapse — several can be open at once.
  private readonly expandedHeadlines = toggleSet();

  isHeadlineExpanded(index: number): boolean {
    return this.expandedHeadlines.has(index);
  }

  toggleHeadline(index: number) {
    this.expandedHeadlines.toggle(index);
  }

  private parseHeadlineDate(date: string | null | undefined): number {
    if (!date) return Number.NEGATIVE_INFINITY;
    const match = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(date);
    if (!match) return Number.NEGATIVE_INFINITY;
    const [, day, month, year] = match;
    return Date.UTC(Number(year), Number(month) - 1, Number(day));
  }
}
